import { useMemo, useState } from "react";
import { ChevronRight, Eye, EyeOff, Crosshair, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

/** Depth-first flatten into render rows, honouring collapsed parents. */
function toRows(nodes, collapsed, expandAll, depth = 0, rows = []) {
  (nodes || []).forEach((node) => {
    const children = node.children || [];
    const open = expandAll || !collapsed.has(node.path);
    rows.push({ ...node, depth, hasChildren: children.length > 0, open });
    if (children.length && open) toRows(children, collapsed, expandAll, depth + 1, rows);
  });
  return rows;
}

function flatten(nodes, out = []) {
  (nodes || []).forEach((node) => {
    out.push(node);
    flatten(node.children, out);
  });
  return out;
}

/** Right panel: searchable nested block hierarchy with visibility controls. */
export function NestedBlockTree({
  tree,
  hidden,
  activeNode,
  onToggle,
  onSelect,
  onShowAll,
  onHideAll,
  onIsolate,
}) {
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState(new Set());
  const needle = query.trim().toLowerCase();

  const rows = useMemo(() => {
    if (needle)
      return flatten(tree)
        .filter((node) => node.name.toLowerCase().includes(needle))
        .map((node) => ({ ...node, depth: 0, hasChildren: false, open: true }));
    return toRows(tree, collapsed, false);
  }, [tree, collapsed, needle]);

  const toggleCollapse = (path) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  if (!tree.length)
    return (
      <p
        data-testid="nested-empty-message"
        className="rounded-lg border border-slate-300 bg-white px-3 py-3 text-[11.5px] text-slate-600"
      >
        No nested blocks found in this panel type.
      </p>
    );

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <div className="relative">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input
          data-testid="nested-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search nested blocks…"
          className="h-8 border-slate-300 bg-white pl-7 text-[11px] text-slate-800 placeholder:text-slate-500"
        />
      </div>
      <div className="flex gap-1.5">
        <button
          data-testid="nested-show-all"
          onClick={onShowAll}
          className="mono flex h-7 flex-1 items-center justify-center gap-1 rounded-full border border-slate-300 text-[9.5px] uppercase tracking-wider text-slate-600 transition-colors hover:border-cyan-500/60 hover:text-cyan-700"
        >
          <Eye size={11} /> All
        </button>
        <button
          data-testid="nested-hide-all"
          onClick={onHideAll}
          className="mono flex h-7 flex-1 items-center justify-center gap-1 rounded-full border border-slate-300 text-[9.5px] uppercase tracking-wider text-slate-600 transition-colors hover:border-cyan-500/60 hover:text-cyan-700"
        >
          <EyeOff size={11} /> None
        </button>
        <button
          data-testid="nested-isolate"
          onClick={onIsolate}
          disabled={!activeNode}
          className="mono flex h-7 flex-1 items-center justify-center gap-1 rounded-full border border-slate-300 text-[9.5px] uppercase tracking-wider text-slate-600 transition-colors hover:border-cyan-500/60 hover:text-cyan-700 disabled:opacity-40"
        >
          <Crosshair size={11} /> Isolate
        </button>
      </div>
      <ul className="okn-scroll min-h-0 flex-1 overflow-y-auto pr-1" data-testid="nested-tree">
        {rows.map((row) => {
          const isHidden = hidden.has(row.path);
          const active = activeNode === row.path;
          return (
            <li
              key={row.path}
              data-testid={`nested-node-${row.path}`}
              className={`flex items-center gap-1.5 rounded-md py-1 pr-1.5 transition-colors ${
                active ? "bg-cyan-600/10" : "hover:bg-slate-900/5"
              }`}
              style={{ paddingLeft: `${row.depth * 12 + 4}px` }}
            >
              {row.hasChildren ? (
                <button
                  type="button"
                  data-testid={`nested-expand-${row.path}`}
                  onClick={() => toggleCollapse(row.path)}
                  className="grid h-4 w-4 shrink-0 place-items-center text-slate-500 hover:text-slate-900"
                >
                  <ChevronRight
                    size={12}
                    className={`transition-transform ${row.open ? "rotate-90" : ""}`}
                  />
                </button>
              ) : (
                <span className="h-4 w-4 shrink-0" />
              )}
              <button
                type="button"
                data-testid={`nested-select-${row.path}`}
                onClick={() => onSelect(active ? null : row.path)}
                title={row.path}
                className="min-w-0 flex-1 truncate text-left text-[11.5px] text-slate-800"
              >
                {row.name}
              </button>
              <span className="mono shrink-0 text-[10px] text-slate-500">×{row.count}</span>
              <button
                type="button"
                data-testid={`nested-visibility-${row.path}`}
                onClick={() => onToggle(row.path)}
                title={isHidden ? "Show" : "Hide"}
                className="grid h-5 w-5 shrink-0 place-items-center rounded text-slate-500 hover:bg-slate-900/10 hover:text-slate-900"
              >
                {isHidden ? <EyeOff size={12} /> : <Eye size={12} />}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
